<script setup lang="ts">

import { usePlanetStore } from '@/stores/planetStore.ts'
import { computed, defineProps } from 'vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePQAStore } from '@/stores/PQAStore.ts'
import Backend from '@/models/backend.ts'
import ToggleSwitch from '@/components/Elements/ToggleSwitch/ToggleSwitch.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore();
const gameStore = useGameStore();
const PQAStore = usePQAStore()
const backend = new Backend()

/**
 * Functions
 */
const createPQA = () => {
  gameStore.modalType='createPQA';
};

const enablePQA = (id:number) => {
  backend.planetSetPQA([props.planetId],id).then(() => {
    gameStore.setTinySuccessToastMessage(t('planets.production.automatedQueues.enabled'));
  })
};

const disablePQA = () => {
  backend.planetSetPQA([props.planetId],0).then(() => {
    gameStore.setTinySuccessToastMessage(t('planets.production.automatedQueues.disabled'));
  })
};

const configurePQA = (id:number) => {
  gameStore.modalType = 'configPQA';
  gameStore.modalData = id;
};

const renamePQA = (id: number) => {
  gameStore.modalType = 'renamePQA';
  gameStore.modalData = id;
};

const deletePQA = (id: number) => {
  gameStore.modal2Type = 'confirmationDelete';
  gameStore.modal2Data = {
    title: t('planets.production.automatedQueues.deleteConfirm.title'),
    message: t('planets.production.automatedQueues.deleteConfirm.message'),
    actionText: t('planets.production.automatedQueues.deleteConfirm.delete'),
    cancelText: t('planets.production.automatedQueues.deleteConfirm.cancel'),
    action: () => {
      backend.deletePQA(id).then(() => {
        gameStore.setTinySuccessToastMessage(t('planets.production.automatedQueues.deleted'));
      });
    },
  };
};

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)

const allQueues = computed(() => {
  return [...PQAStore.queues.values()];
});

const isQueueActive = (queueId: number) => {
  return planetData.value.planetQueueAutomationId === queueId;
};

const toggleQueue = (queueId: number) => {
  if (isQueueActive(queueId)) {
    disablePQA();
  } else {
    enablePQA(queueId);
  }
};

</script>

<template>

  <StandardCard v-if="allQueues.length > 0" :title="t('planets.production.automatedQueues.title')" :help="t('planets.production.automatedQueues.help')" topPadding="2">
    <template v-for="(pqa, index) in allQueues" :key="pqa.id">
      <div :class="['flex justify-between items-center', { 'pt-2 mt-2 border-t border-t-slate-700': index > 0 }]">
        <div class="flex-grow flex items-center">
          <div class="text-subtext">{{ pqa.name }}</div>
        </div>
        <div class="flex items-center gap-2">
          <div class="flex items-center -mt-1">
            <ToggleSwitch :model-value="isQueueActive(pqa.id)" @update:model-value="toggleQueue(pqa.id)" />
          </div>
          <div class="flex items-center">
            <ActionButton @click="configurePQA(pqa.id)" :for-icon="true">
              <GenericIcon :tooltip="t('planets.production.automatedQueues.configure')" type="cog" />
            </ActionButton>
          </div>
          <div class="flex items-center">
            <ActionButton @click="renamePQA(pqa.id)" :for-icon="true">
              <GenericIcon :tooltip="t('planets.production.automatedQueues.rename')" type="edit" />
            </ActionButton>
          </div>
          <div class="flex items-center">
            <ActionButton @click="deletePQA(pqa.id)" :for-icon="true">
              <GenericIcon :tooltip="t('planets.production.automatedQueues.delete')" type="trash" />
            </ActionButton>
          </div>
        </div>
      </div>
    </template>
  </StandardCard>

  <StandardCard top-padding="2">
    <ActionButton @click="createPQA" :full-width="true">{{ t('planets.production.createNewQueue') }}</ActionButton>
  </StandardCard>

</template>

<style scoped lang="scss">

</style>